
import { useEffect, useState } from "react";
import useTasks from "../../../hooks/useTasks";
import useDisplayNotifications from "../../../hooks/useDisplayNotifications";
import { ITask } from "../../../data_models/task";
import { IFreelancer } from "../../../data_models/freelancer";
import Lightbox from "../../common/Lightbox";
import LoadingIndicator from "../../common/LoadingIndicator";
import { DisplayNotificationType } from "../../../models/DisplayNotification";
import useProjects from "../../../hooks/useProjects";
import { EProjectStatus, IProject } from "../../../data_models/project";
import { Chip } from "../../common/Chip";
import TextInputField from "../../common/TextInputField";
import { FilledButton } from "../../common/Button";
import useInvitations from "../../../hooks/useInvitations";
import { getFreelancerName } from "../../../utils/freelancerUtils";
import Accordion from "../../common/Accordion";
import useConfig from "../../../hooks/useConfig";
import RichText from "../../common/RichText";


interface InviteToTasksLightboxProps {
    onClose: () => void;
    freelancer: IFreelancer;
}

const InviteToTasksLightbox: React.FC<InviteToTasksLightboxProps> = ({ onClose, freelancer }) => {

    const [loading, setLoading] = useState(false);
    const [loadingInvite, setLoadingInvite] = useState(false);

    const [tasks, setTasks] = useState<ITask[]>([]);
    const [projects, setProjects] = useState<IProject[]>([]);
    const [selectedTask, setSelectedTask] = useState<ITask>();

    const [projectTasksMap, setProjectTasksMap] = useState<Map<string, ITask[]>>(new Map());

    const { fetchUnassignedTasksForPm } = useTasks();
    const { fetchMyProjectsAsPm } = useProjects();
    const { newInvitation } = useInvitations();
    const { addNotification } = useDisplayNotifications();
    const { config } = useConfig();

    useEffect(() => {
        setLoading(true);
        let fetchProjectsDone = false;
        let fetchTasksDone = false;

        fetchMyProjectsAsPm(1, 99999).then(projects => {
            // filter ongoing projects
            const ongoingProjects = projects.filter(project => project.status &&
                project.status >= EProjectStatus.WAITING_FOR_PM &&
                project.status <= EProjectStatus.COMPLETED);

            setProjects(ongoingProjects);
        }).finally(() => {
            fetchProjectsDone = true;
            if (fetchProjectsDone && fetchTasksDone) {
                setLoading(false);
            }
        });

        fetchUnassignedTasksForPm(1, 99999).then(tasks => {
            setTasks(tasks);

            const projectTasksMap = new Map<string, ITask[]>();
            tasks.forEach(task => {
                const projectId = task.projectId;
                if (projectTasksMap.has(projectId!)) {
                    projectTasksMap.get(projectId!)?.push(task);
                } else {
                    projectTasksMap.set(projectId!, [task]);
                }
            });
            setProjectTasksMap(projectTasksMap);
        }).finally(() => {
            fetchTasksDone = true;
            if (fetchProjectsDone && fetchTasksDone) {
                setLoading(false);
            }
        });
    }, [])


    const onInvite = async () => {
        if (!selectedTask) return;

        setLoadingInvite(true);
        try {
            const payout: number = selectedTask.estimatedCredits || 0;
            if (freelancer._id &&
                selectedTask?._id &&
                getProjectByTaskId(selectedTask._id!)!._id &&
                payout
            ) {
                const invite = await newInvitation(
                    freelancer._id!,
                    selectedTask._id!,
                    getProjectByTaskId(selectedTask._id!)!._id!,
                    payout);
                addNotification({
                    message: "Invitation successfully sent",
                    type: DisplayNotificationType.SUCCESS,
                })
            } else {
                throw new Error("An unknown error occured");
            }
            onClose();

        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR,
            })
        } finally {
            setLoadingInvite(false);
        }

    }

    const getProjectByTaskId = (taskId: string) => {
        const project = projects.find(project => project.tasks?.some(task => task === taskId));
        return project;
    }

    const onSelectTask = (task: ITask) => {
        if (selectedTask?._id === task._id) {
            setSelectedTask(undefined);
        } else {
            setSelectedTask(task);
        }
    }


    return (
        <Lightbox
            content={
                <div className="" >
                    <h3>Offer Tasks</h3>
                    <p className="mt-4">Select one or more tasks you’d like to offer {getFreelancerName(freelancer)} to. You can withdraw the offer at any time before they accept. Once {getFreelancerName(freelancer)} accepts, they’ll become part of the project and can start working right away!</p>
                    <div>

                        {loading && projectTasksMap
                            ? <div className=""><LoadingIndicator /></div>
                            : <div>
                                {projects.map(project => (
                                    <div key={project._id} className="my-4">
                                        <Accordion
                                            title={
                                                <div>
                                                    <p className="font-bold">{project.title}</p>
                                                    <p className={`caption2 italic ${(projectTasksMap.get(project._id!)?.length || 0) === 0
                                                        ? ''
                                                        : 'font-bold'}`}>{projectTasksMap.get(project._id!)?.length || 0} available tasks</p>
                                                </div>
                                            }
                                            content={

                                                <div className="flex flex-col space-y-2">{projectTasksMap.get(project._id!)
                                                    ? projectTasksMap.get(project._id!)?.map(task => (
                                                        <div
                                                            key={task._id}
                                                            onClick={() => { onSelectTask(task) }}
                                                            className={`
                                                                cursor-pointer px-4 py-2 rounded-md hover:bg-neutral-100
                                                                ${selectedTask?._id === task._id ? 'border border-primary' : ''}`}
                                                        >
                                                            <p className="font-bold">{task.title}</p>
                                                            <p className="caption2">{`Credits: ${task.estimatedCredits} (${(task.estimatedCredits || 0) * (config?.rateCreditsToUsd.freelancer || 0)} USD)`}</p>
                                                            <div className="my-2 flex flex-wrap gap-2">
                                                                {task.requiredSkills?.map(skill => (
                                                                    <div key={skill}>
                                                                        <Chip>{skill}</Chip>
                                                                    </div>
                                                                ))}
                                                                {task.requiredTools?.map(tool => (
                                                                    <div key={tool}>
                                                                        <Chip theme="secondary">{tool}</Chip>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <RichText
                                                                text={task.description || ""}
                                                                className="mt-2"
                                                            />
                                                        </div>
                                                    ))
                                                    : <p className="caption2">This project has currently no available tasks</p>}
                                                </div>
                                            }
                                            theme="primary"
                                            highlightWhenOpen={true}
                                        />
                                    </div>
                                ))}

                                <div>
                                    {
                                        <div className="flex flex-col items-center space-y-4">
                                            <FilledButton
                                                fullWidth
                                                isLoading={loadingInvite}
                                                onClick={onInvite}
                                                disabled={!selectedTask}
                                            >{selectedTask ? `Offer ${selectedTask.title}` : "Select a task"}</FilledButton>
                                        </div>}
                                </div>
                            </div>
                        }
                    </div>

                </div>}
            onClose={onClose}
        />

    );
}

export default InviteToTasksLightbox;